import React from "react";
import { GENERATOR_CODES } from "@csk/core";
import {
  DemoWorkbenchCard,
  DemoWorkbenchFact,
  DemoWorkbenchFactGrid,
  DemoWorkbenchPill,
  DemoWorkbenchTable,
} from "@csk/demo-ui";
import type { DeltaDiff } from "../types";

function joinOrDash(items: readonly string[]): string {
  return items.length > 0 ? items.join(", ") : "—";
}

function signed(delta: number): string {
  return delta >= 0 ? `+${delta}` : String(delta);
}

export function DeltaDiffPanel(props: {
  diff: DeltaDiff | null;
}): React.JSX.Element {
  if (!props.diff) {
    return (
      <DemoWorkbenchCard
        title="No diff loaded"
        right={<DemoWorkbenchPill>optional</DemoWorkbenchPill>}
      >
        <div className="text-sm text-slate-300">
          The delta-diff is derived in <code>useData.ts</code> from the bridges
          catalog plus the canonical missing-bridge fixture; one of those inputs
          failed to parse. See the dashboard error banner for the underlying
          mismatch.
        </div>
      </DemoWorkbenchCard>
    );
  }

  const d = props.diff;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <DemoWorkbenchCard title="Bridge delta (δ change)">
        <div className="text-sm text-slate-300">
          Added: {joinOrDash(d.added_bridges)}
        </div>
        <div className="text-sm text-slate-300 mt-1">
          Removed: {joinOrDash(d.removed_bridges)}
        </div>
      </DemoWorkbenchCard>

      <DemoWorkbenchCard title="Footprint movement (G1–G4)">
        <DemoWorkbenchFactGrid minColumnWidth={110}>
          {GENERATOR_CODES.map((axis) => (
            <DemoWorkbenchFact
              key={axis}
              label={axis}
              value={
                <>
                  {d.footprint_A[axis]} → {d.footprint_B[axis]}{" "}
                  <span className="text-slate-400">
                    ({signed(d.footprint_delta[axis])})
                  </span>
                </>
              }
              tone={d.footprint_delta[axis] === 0 ? "default" : "accent"}
            />
          ))}
        </DemoWorkbenchFactGrid>
        <div className="text-xs text-slate-400 mt-2">
          This is the “differential security” view: exploitability changes only
          when δ moves.
        </div>
      </DemoWorkbenchCard>

      <DemoWorkbenchCard
        title="Matrix rule changes"
        right={<DemoWorkbenchPill>{d.rule_changes.length}</DemoWorkbenchPill>}
      >
        <DemoWorkbenchTable
          headers={["lhs", "rhs", "from", "to"]}
          rows={d.rule_changes.map((r) => [
            r.lhs,
            r.rhs,
            r.from ?? "",
            r.to ?? "",
          ])}
        />
      </DemoWorkbenchCard>

      <DemoWorkbenchCard title="Interpretation">
        <div className="text-sm text-slate-300 space-y-2">
          <div>δ-diff is the only way representability changes.</div>
          <div>
            Matrix changes alter compositional closure (new paths become
            admissible/forbidden).
          </div>
          <div>Footprint deltas provide measurable posture movement.</div>
        </div>
      </DemoWorkbenchCard>
    </div>
  );
}
